import { normalizeMacAddress } from '~/Common/StringOperations';
import type { MenuItem } from 'primevue/menuitem';
import type { UserRole, UserGroup, User, ClientView, ConfigProfiles, ClientGroup, Tenant } from '~/types/graphql';
import { Config_Mode } from '~/types/graphql';
import { useI18n } from 'vue-i18n';
import { usePermissionsStore } from '~/stores/permissions';

/**
 * Generates a context menu for user roles.
 * @param router - The router instance for navigation.
 * @param handleSendAction - Function to handle sending actions.
 */
export function UserRolesContextMenu(router: any, handleSendAction: Function) {
  const { t } = useI18n();
  const permissionsStore = usePermissionsStore();

  // Hide menu if only read permission
  const hasWrite = permissionsStore.canWrite('role');

  return (role: UserRole) => hasWrite ? [
    {
      label: t('common.actions.edit'),
      icon: 'pi pi-pencil',
      entity: 'Role',
      name: role.name,
      command: () => router.push({
        name: 'roles-roleid-edit',
        params: { roleid: role.id }
      }),
    },
    {
      label: t('common.actions.delete'),
      icon: 'pi pi-trash',
      entity: 'Role',
      name: role.name,
      command: () => handleSendAction('deleteUserRole', role.id),
    },
  ] : [];
}

/**
 * Generates a context menu for user groups.
 * @param router - The router instance for navigation.
 * @param handleSendAction - Function to handle sending actions.
 */
export function UserGroupsContextMenu(router: any, handleSendAction: Function) {
  const { t } = useI18n();
  const permissionsStore = usePermissionsStore();
  const hasWrite = permissionsStore.canWrite('userGroup');
  return (userGroup: UserGroup) => hasWrite ? [
    {
      label: t('common.actions.edit'),
      icon: 'pi pi-pencil',
      entity: 'User group',
      name: userGroup.name,
      command: () => router.push({
        name: 'userGroups-groupid-edit',
        params: { groupid: userGroup.id }
      }),
    },
    {
      label: t('common.actions.assignProfile'),
      icon: 'pi pi-id-card',
      entity: 'User group',
      name: userGroup.name,
      command: () => handleSendAction('assignProfile', userGroup.id),
    },
    {
      label: t('common.actions.delete'),
      icon: 'pi pi-trash',
      entity: 'User group',
      name: userGroup.name,
      command: () => handleSendAction('deleteUserGroup', userGroup.id),
    },
  ] : [];
}

/**
 * Generates a context menu for users.
 * @param router - The router instance for navigation.
 * @param handleSendAction - Function to handle sending actions.
 */
export function UsersContextMenu(router: any, handleSendAction: Function) {
  const { t } = useI18n();
  const permissionsStore = usePermissionsStore();
  const hasWrite = permissionsStore.canWrite('user');
  return (user: User) => hasWrite ? [
    {
      label: t('common.actions.edit'),
      icon: 'pi pi-pencil',
      entity: 'User',
      name: user.fullName,
      command: () => router.push({
        name: 'users-id-edit',
        params: { id: user.id }
      }),
    },
    {
      label: t('common.actions.assignProfile'),
      icon: 'pi pi-id-card',
      entity: 'User',
      name: user.fullName,
      command: () => handleSendAction('assignProfile', user.id),
    },
    {
      label: t('common.actions.delete'),
      icon: 'pi pi-trash',
      entity: 'User',
      name: user.fullName,
      command: () => handleSendAction('deleteUser', user.id),
    },
  ]: []
}
export function TenantsContextMenu(router: any, handleSendAction: Function) {
  const { t } = useI18n();
  return (tenant: Tenant) => [
    {
      label: t('common.actions.edit'),
      icon: 'pi pi-pencil',
      entity: 'Tenant',
      name: tenant.name,
      command: () => router.push({
        name: 'tenants-id-edit',
        params: { id: tenant.id }
      }),
    },
    {
      label: t('common.actions.delete'),
      icon: 'pi pi-trash',
      entity: 'Tenant',
      name: tenant.name,
      command: () => handleSendAction('deleteTenants', tenant.id),
    },
  ];
}

export function ConfigProfilesContextMenu(router: any, handleSendAction: Function) {
  const { t } = useI18n();
  const permissionsStore = usePermissionsStore();
  const hasWrite = permissionsStore.canWrite('configProfile');
  return(profile: ConfigProfiles ) => hasWrite ? [
    {
      label: t('common.actions.edit'),
      icon: 'pi pi-pencil',
      entity: 'Profile',
      command: () => router.push({
        name: 'configProfile-profileid-edit',
        params: { profileid: profile.id }
      }),
    },
    {
      label: t('common.actions.delete'),
      icon: 'pi pi-trash',
      entity: 'Profile',
      command: () => handleSendAction('deleteConfigProfile', profile.id),
      },
  ] : [];
}

/**
 * Generates a context menu for groups.
 * @param router - The router instance for navigation.
 * @param handleSendAction - Function to handle sending actions to the group.
 * @param sendMessage - Function to send a message to the group
 * @param confirmation - Function to handle confirmation actions.
 */
export function GroupsContextMenu(router: any, handleSendAction: Function, sendMessage?: Function, confirmation?: Function) {
  const {t} = useI18n();
  const permissionsStore = usePermissionsStore();
  function hasGroupWrite(group: ClientGroup) {
    if (permissionsStore.hasExactPermission('write:clientGroup')) return true;
    const groupName = group?.name;
    return permissionsStore.canWrite('clientGroup', groupName);
  }
  function hasConfigWrite(group: ClientGroup) {
    if (permissionsStore.hasExactPermission('write:config')) return true;
    const groupName = group?.name;
    return permissionsStore.canWrite('config', groupName);
  }
  function hasConfigProfileWrite(group: ClientGroup) {
    if (permissionsStore.hasExactPermission('write:configProfile')) return true;
    const groupName = group?.name;
    return permissionsStore.canWrite('configProfile', groupName);
  }
  return (group: ClientGroup) => {
    if (!hasGroupWrite(group)) return [];
    const menu: MenuItem[] = [
      {
        label: t('common.actions.edit'),
        icon: 'pi pi-pencil',
        command: () => router.push({
          name: "clientGroups-groupid-edit",
          params: { groupid: group.id }
        }),
      },
      {
        label: t('common.actions.delete'),
        icon: 'pi pi-trash',
        command: () => handleSendAction('deleteGroup', group.id),
      },
    ];
    if (hasConfigWrite(group)) {
      const hasProductionConfig = Boolean((group as any)?.config?.id);
      const hasStagingConfig = Boolean((group as any)?.stagingConfig?.id);

      if (hasProductionConfig && hasStagingConfig) {
        menu.push({
          label: t('group.dataview.editConfig'),
          icon: 'pi pi-cog',
          items: [
            {
              label: t('common.configMode.production'),
              command: () =>
                handleSendAction(
                  'editGroupConfig',
                  group.id,
                  '',
                  undefined,
                  Config_Mode.Production,
                ),
            },
            {
              label: t('common.configMode.staging'),
              command: () =>
                handleSendAction(
                  'editGroupConfig',
                  group.id,
                  '',
                  undefined,
                  Config_Mode.Staging,
                ),
            },
          ],
        });
      } else {
        const selectedMode = hasStagingConfig
          ? Config_Mode.Staging
          : Config_Mode.Production;

        menu.push({
          label: t('group.dataview.editConfig'),
          icon: 'pi pi-cog',
          command: () => {
            console.debug('[ContextMenu] editConfig clicked for group', group.id);
            handleSendAction(
              'editGroupConfig',
              group.id,
              '',
              undefined,
              selectedMode,
            );
          },
        });
      }

      if (hasStagingConfig) {
        menu.push({
          label: t('group.dataview.promoteStagingConfig'),
          icon: 'pi pi-upload',
          command: () => handleSendAction('promoteStagingConfig', group.id),
        });
      }
    }
    if (hasConfigProfileWrite(group)) {
      menu.push({
        label: t('group.dataview.createProfile'),
        icon: 'pi pi-plus',
        command: () => handleSendAction('createProfile', group.id),
      });
    }
    // Only show actionScheduler if user has permission
    if (permissionsStore.canScheduleAction({ groupName: group?.name })) {
      menu.push({
        label: t('group.dataview.actionScheduler'),
        icon: 'pi pi-clock',
        command: () => handleSendAction('actionScheduler', group.id),
      });
    }
    menu.push({
      label: t('group.dataview.assignProfile'),
      icon: 'pi pi-id-card',
      command: () => handleSendAction('assignProfile', group.id),
    });
    return menu;
  };
}


export function BulkActionsContextMenu(router: any, handleSendAction: Function, sendMessage: Function) {
  const { t } = useI18n();
  const permissionsStore = usePermissionsStore();

  const getClientMacAddress = (client: ClientView) => {
    if (!Array.isArray(client?.networkInterfaces)) return '';
    const iface = client.networkInterfaces.find((networkInterface) => networkInterface?.macAddress);
    return iface?.macAddress ? normalizeMacAddress(iface.macAddress) : '';
  };

  // Accepts an array of selected clients for bulk actions
  return (selectedClients: ClientView[]) => {
    if (selectedClients.length === 0) return [];

    const menu: any[] = [];

    // Helper to check if user can assign profiles for all selected clients
    const canAssignProfileForAllClients = () => selectedClients.every(client =>
      permissionsStore.canWrite('clientGroup', client.clientGroup?.name) ||
      permissionsStore.canWrite('client', client.serialNumber)
    );

    // Helper to check if user can schedule action for all selected clients
    const canScheduleActionForAllClients = () => selectedClients.every(client => {
      const macAddress = getClientMacAddress(client);
      return permissionsStore.canScheduleAction({
        groupName: client.clientGroup?.name,
        macAddress
      });
    });

    const canExecuteActionForAllClients = (actionType: string) =>
      selectedClients.every((client) =>
        permissionsStore.canExecuteAction({
          actionType,
          groupName: client.clientGroup?.name,
          macAddress: getClientMacAddress(client),
        })
      );

    const bulkActionItems = [
      canExecuteActionForAllClients('factory_default') ? {
        label: t('common.contextMenu.factoryDefault'),
        command: () => {
          console.log('Bulk menu: factoryDefault', selectedClients.map(c => c.id));
          handleSendAction('factory_default', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('configuration_default') ? {
        label: t('common.contextMenu.configurationReset'),
        command: () => {
          console.log('Bulk menu: configurationReset', selectedClients.map(c => c.id));
          handleSendAction('configuration_default', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('message') ? {
        label: t('common.contextMenu.message'),
        command: () => {
          console.log('Bulk menu: message', selectedClients);
          sendMessage(selectedClients);
        },
      } : null,
      canExecuteActionForAllClients('reboot') ? {
        label: t('common.contextMenu.reboot'),
        command: () => {
          console.log('Bulk menu: reboot', selectedClients.map(c => c.id));
          handleSendAction('reboot', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('force_register') ? {
        label: t('common.contextMenu.forceRegister'),
        command: () => {
          console.log('Bulk menu: forceRegister', selectedClients.map(c => c.id));
          handleSendAction('force_register', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('factory_screen_defaults') ? {
        label: t('common.contextMenu.factoryScreenDefaults'),
        command: () => {
          console.log('Bulk menu: factoryScreenDefaults', selectedClients.map(c => c.id));
          handleSendAction('factory_screen_defaults', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('shutdown') ? {
        label: t('common.contextMenu.shutdown'),
        command: () => {
          console.log('Bulk menu: shutdown', selectedClients.map(c => c.id));
          handleSendAction('shutdown', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('standby') ? {
        label: t('common.contextMenu.standby'),
        command: () => {
          console.log('Bulk menu: standby', selectedClients.map(c => c.id));
          handleSendAction('standby', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('update_certificates') ? {
        label: t('common.contextMenu.updateCertificates'),
        command: () => {
          console.log('Bulk menu: updateCertificates', selectedClients.map(c => c.id));
          handleSendAction('update_certificates', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('wakeup') ? {
        label: t('common.contextMenu.wakeup'),
        command: () => {
          console.log('Bulk menu: wakeup', selectedClients.map(c => c.id));
          handleSendAction('wakeup', selectedClients.map(c => c.id));
        },
      } : null,
      canExecuteActionForAllClients('request_info') ? {
        label: t('common.contextMenu.requestInfo'),
        command: () => {
          console.log('Bulk menu: requestInfo', selectedClients.map(c => c.id));
          handleSendAction('request_info', selectedClients.map(c => c.id));
        },
      } : null,
    ].filter(Boolean);

    // Add assignProfile only if permission exists for all clients
    if (canAssignProfileForAllClients()) {
      menu.push({
        label: t('common.actions.assignProfile'),
        icon: 'pi pi-id-card',
        command: () => {
          console.log('Bulk menu: assignProfile', selectedClients.map(c => c.id));
          handleSendAction('assignProfile', selectedClients.map(c => c.id));
        },
      });
    }

    // Add actionScheduler only if permission exists for all clients
    if (canScheduleActionForAllClients()) {
      menu.push({
        label: t('group.dataview.actionScheduler'),
        icon: 'pi pi-clock',
        command: () => {
          console.log('Bulk menu: actionScheduler', selectedClients.map(c => c.id));
          handleSendAction('actionScheduler', selectedClients.map(c => c.id));
        },
      });
    }

    // Add "more" submenu only if user can execute at least one action for all clients
    if (bulkActionItems.length > 0) {
      menu.push({
        label: t('common.contextMenu.more'),
        items: bulkActionItems,
      });
    }

    return menu;
  };
}

/**
 * Generates a context menu for clients.
 * @param router - The router instance for navigation.
 * @param handleSendAction - Function to handle sending actions to the client.
 * @param sendMessage - Function to send a message to the client.
 */
export function ClientsContextMenu(router: any, handleSendAction: Function, sendMessage: Function) {
  const { t } = useI18n();
  const permissionsStore = usePermissionsStore();
  const buildKommboxUrl = (client: ClientView) => {
    const mac =
      client?.networkInterfaces?.find((iface) => iface?.macAddress)
        ?.macAddress ?? '';
    if (!mac) return '';
    return `/api/tcms/kommboxProxy/${encodeURIComponent(mac)}/index.php`;
  };
  const getClientMacAddress = (client: ClientView) => {
    if (!Array.isArray(client?.networkInterfaces)) return '';
    const iface = client.networkInterfaces.find((networkInterface) => networkInterface?.macAddress);
    return iface?.macAddress ? normalizeMacAddress(iface.macAddress) : '';
  };
  function hasClientWrite(client: ClientView) {
    if (permissionsStore.hasExactPermission('write:client')) return true;
    if (Array.isArray(client?.networkInterfaces)) {
      for (const iface of client.networkInterfaces) {
        if (iface?.macAddress && permissionsStore.hasClientWritePermission(iface.macAddress)) {
          return true;
        }
      }
    }
    // Allow write:clientGroup or write:clientGroup:groupName
    if (permissionsStore.hasExactPermission('write:clientGroup')) return true;
    const groupName = client.clientGroup?.name;
    if (groupName && permissionsStore.hasExactPermission(`write:clientGroup:${groupName}`)) {
      return true;
    }
    return false;
  }
  function canPullConfig(client: ClientView) {
    // Accepts either write:clientGroup or write:clientGroup:groupName
    if (permissionsStore.hasExactPermission('write:clientGroup')) return true;
    // Try to get group name from client object
    const groupName = client.clientGroup?.name;
    if (groupName && permissionsStore.hasExactPermission(`write:clientGroup:${groupName}`)) {
      return true;
    }
    return false;
  }
  function canAssignProfile(client: ClientView) {
    // Check if user can write to the client or its group
    if (Array.isArray(client?.networkInterfaces)) {
      for (const iface of client.networkInterfaces) {
        if (iface?.macAddress && permissionsStore.hasClientWritePermission(iface.macAddress)) {
          return true;
        }
      }
    }
    if (permissionsStore.hasExactPermission('write:client')) return true;
    if (permissionsStore.hasExactPermission('write:clientGroup')) return true;
    const groupName = client.clientGroup?.name;
    if (groupName && permissionsStore.hasExactPermission(`write:clientGroup:${groupName}`)) {
      return true;
    }
    return false;
  }
  return (client: ClientView) => {
    const menu = [];
    const macAddress = getClientMacAddress(client);
    const clientGroupName = client.clientGroup?.name;
    const canEditClient = hasClientWrite(client);

    if (canEditClient && canPullConfig(client)) {
      menu.push({
        label: t('common.contextMenu.configLoad'),
        icon: 'pi pi-cloud-download',
        items: [
          {
            label: t('common.configMode.production'),
            command: () => handleSendAction('pullConfig', client?.id, '', undefined, Config_Mode.Production),
          },
          {
            label: t('common.configMode.staging'),
            command: () => handleSendAction('pullConfig', client?.id, '', undefined, Config_Mode.Staging),
          },
        ],
      });
    }
    if (canEditClient) {
      menu.push(
        {
        label: t('common.contextMenu.kommBox'),
        icon: 'pi pi-external-link',
        command: () => {
          const url = buildKommboxUrl(client);
          if (url) {
            window.open(url, '_blank', 'noopener');
          }
        },
      },
      {
        label: t('common.actions.edit'),
        icon: 'pi pi-pencil',
        command: () => router.push({ name: 'clients-clientid-edit', params: { clientid: client?.id } }),
      },
      );
    }

    if (canEditClient && canAssignProfile(client)) {
      menu.push({
        label: t('common.actions.assignProfile'),
        icon: 'pi pi-id-card',
        command: () => handleSendAction('assignProfile', client.id),
      });
    }

    if (permissionsStore.canScheduleAction({ macAddress })) {
      menu.push({
        label: t('group.dataview.actionScheduler'),
        icon: 'pi pi-clock',
        command: () => handleSendAction('actionScheduler', client?.id),
      });
    }

    const actionItems = [
      permissionsStore.canExecuteAction({ actionType: 'message', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.message'),
        icon:'pi pi-comments',
        command: () => sendMessage(client),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'force_register', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.forceRegister'),
        icon:'pi pi-user-plus',
        command: () => handleSendAction('force_register', client?.id),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'update_certificates', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.updateCertificates'),
        icon: 'pi pi-shield',
        command: () => handleSendAction('update_certificates', client?.id),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'factory_screen_defaults', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.factoryScreenDefaults'),
        icon:'pi pi-desktop',
        command: () => handleSendAction('factory_screen_defaults', client?.id),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'reboot', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.reboot'),
        icon:'pi pi-replay',
        command: () => handleSendAction('reboot', client?.id),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'standby', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.standby'),
        icon:'pi pi-moon',
        command: () => handleSendAction('standby', client),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'wakeup', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.wakeup'),
        icon:'pi pi-sun',
        command: () => handleSendAction('wakeup', client?.id, '', 'showDelegateSelector'),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'shutdown', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.shutdown'),
        icon:'pi pi-power-off',
        command: () => handleSendAction('shutdown', client?.id),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'request_info', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.requestInfo'),
        icon:'pi pi-info-circle',
        command: () => handleSendAction('request_info', client?.id),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'configuration_default', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.configurationReset'),
        icon:'pi pi-history',
        command: () => handleSendAction('configuration_default', client?.id),
      } : null,
      permissionsStore.canExecuteAction({ actionType: 'factory_default', groupName: clientGroupName, macAddress }) ? {
        label: t('common.contextMenu.factoryDefault'),
        icon: 'pi pi-cog',
        command: () => handleSendAction('factory_default', client?.id),
      } : null,
    ].filter(Boolean);

    if (actionItems.length > 0) {
      menu.push({
        label: t('common.contextMenu.more'),
        items: actionItems,
      });
    }

    return menu;
  };
}

/**
 * Generates a context menu for config changes dialog.
 * @param selectedChangesCount - The number of selected changes.
 * @param flattenedChangesCount - The total number of changes.
 * @param handleSendAction - Function to handle sending actions.
 */
export function ConfigChangesContextMenu(
  selectedChangesCount: number,
  flattenedChangesCount: number,
  handleSendAction: Function
) {
  const { t } = useI18n();

  return () => [
    {
      label: t('common.actions.selectAll'),
      icon: 'pi pi-check-square',
      disabled: selectedChangesCount === flattenedChangesCount,
      command: () => handleSendAction('selectAll'),
    },
    {
      label: t('common.actions.deselectAll'),
      icon: 'pi pi-stop',
      disabled: selectedChangesCount === 0,
      command: () => handleSendAction('deselectAll'),
    },
    {
      separator: true
    },
    {
      label: t('sidebar.expandAll'),
      icon: 'pi pi-chevron-down',
      command: () => handleSendAction('expandAll'),
    },
    {
      label: t('sidebar.collapseAll'),
      icon: 'pi pi-chevron-right',
      command: () => handleSendAction('collapseAll'),
    }
  ];
}
